import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import Pagination from '@/components/base/Pagination';
import { comics, categories } from '@/mocks/comics';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug = 'hot' } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    setCurrentPage(1);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredComics = useMemo(() => {
    if (slug === 'hot') {
      return [...comics].sort((a, b) => b.views - a.views);
    }
    if (slug === 'new') {
      return [...comics].sort((a, b) => b.updateTime.localeCompare(a.updateTime));
    }
    if (slug === 'completed') {
      return comics.filter((c) => c.status === '已完结');
    }
    return comics.filter((c) => c.categorySlug === slug);
  }, [slug]);

  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const paginatedComics = filteredComics.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const catDesc = `韩漫大全${currentCategory.name}分类 - 收录${currentCategory.count}部相关韩国漫画，免费在线阅读`;

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 韩漫大全`,
    description: catDesc,
    url: `https://hanmandq.com/category/${slug}`,
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}_韩漫大全_韩国漫画分类浏览`}
        description={catDesc}
        keywords={`韩漫大全,${currentCategory.name},韩国漫画,免费在线阅读,漫画网址`}
        canonicalUrl={`https://hanmandq.com/category/${slug}`}
        schemaData={categorySchema}
      />
      <Navbar />
      <main className="pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: currentCategory.name },
            ]}
          />

          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500">{catDesc}</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name} ({cat.count})
              </Link>
            ))}
          </div>

          {paginatedComics.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {paginatedComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    title={comic.title}
                    author={comic.author}
                    cover={comic.cover}
                    status={comic.status}
                    rating={comic.rating}
                    views={comic.views}
                    updateTime={comic.updateTime}
                    category={comic.category}
                  />
                ))}
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          ) : (
            <div className="text-center py-20">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300 w-12 h-12 flex items-center justify-center mx-auto mb-4"></i>
              <p className="text-foreground-500">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}